import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import { LANDING_PAGES } from "@/content/landingPages";
import { getTranslations, type Locale } from "@/i18n/translations";

const BASE_URL = "https://nellove.com";

type Props = { pageId: string; locale: Locale };

const LandingContentPage = ({ pageId, locale }: Props) => {
  const page = LANDING_PAGES.find((p) => p.id === pageId)!;
  const copy = page.content[locale];
  const t = getTranslations(locale);
  const otherLocale: Locale = locale === "en" ? "fr" : "en";
  const canonical = `${BASE_URL}/${locale}/${page.path[locale]}`;
  const altPath = `/${otherLocale}/${page.path[otherLocale]}`;
  const checklistPath = locale === "fr" ? "/fr/checklist-mariage" : "/en/wedding-planning-checklist";

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Nellove", item: `${BASE_URL}/${locale}` },
        { "@type": "ListItem", position: 2, name: copy.heading, item: canonical },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: copy.faq.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    },
  ];

  if (page.isProduct) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Nellove",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `${BASE_URL}/${locale}`,
      description: copy.metaDescription,
      inLanguage: ["en", "fr"],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "CAD",
        description:
          locale === "fr"
            ? "Gratuit pour toujours, sans limite d’invités."
            : "Free forever, with no guest-count limit.",
      },
    });
  }

  return (
    <SiteLayout locale={locale} t={t} altPath={altPath}>
      <Helmet>
        <html lang={locale} />
        <title>{copy.metaTitle}</title>
        <meta name="description" content={copy.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${BASE_URL}/en/${page.path.en}`} />
        <link rel="alternate" hrefLang="fr" href={`${BASE_URL}/fr/${page.path.fr}`} />
        <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en/${page.path.en}`} />
        <meta property="og:title" content={copy.metaTitle} />
        <meta property="og:description" content={copy.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Nellove" />
        <meta property="og:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={copy.metaTitle} />
        <meta name="twitter:description" content={copy.metaDescription} />
        <meta name="twitter:image" content={`${BASE_URL}/images/nellove-cover.png`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="border-b border-stone-200 bg-secondary px-5 py-14 sm:px-6 md:py-20">
        <div className="mx-auto max-w-3xl">
          <p className="text-[12px] font-medium tracking-[0.08em] text-stone-600">{copy.eyebrow}</p>
          <h1 className="mt-3 text-[34px] leading-[1.1] tracking-tight sm:text-[44px]">{copy.heading}</h1>
          <p className="mt-5 text-[17px] leading-relaxed text-stone-700">{copy.intro}</p>
          <a
            href={`/${locale}#join`}
            className="mt-7 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors duration-200 hover:bg-rust-600"
          >
            {copy.ctaButton}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="px-5 py-14 sm:px-6 md:py-16">
        <div className="mx-auto max-w-3xl">
          {copy.sections.map((section) => (
            <article key={section.heading} className="mb-10 last:mb-0">
              <h2 className="font-serif text-[26px] leading-snug tracking-tight">{section.heading}</h2>
              {section.paragraphs?.map((p) => (
                <p key={p} className="mt-4 text-[17px] leading-relaxed text-stone-700">
                  {p}
                </p>
              ))}
              {section.list && (
                <ul className="mt-4 space-y-2">
                  {section.list.map((item) => (
                    <li key={item} className="flex gap-3 text-[17px] leading-relaxed text-stone-700">
                      <span aria-hidden="true" className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-rust-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      <section className="border-t border-stone-200 px-5 py-14 sm:px-6">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-serif text-[26px] tracking-tight">{copy.faqHeading}</h2>
          <dl className="mt-6 space-y-6">
            {copy.faq.map((f) => (
              <div key={f.question} className="rounded-xl border border-stone-200 bg-card p-5 shadow-soft">
                <dt className="font-serif text-[19px] tracking-tight">{f.question}</dt>
                <dd className="mt-2 text-[16px] leading-relaxed text-stone-700">{f.answer}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="px-5 pb-16 sm:px-6">
        <div className="mx-auto max-w-3xl rounded-xl border border-stone-200 bg-secondary p-6">
          <h2 className="font-serif text-[24px] tracking-tight">{copy.ctaHeading}</h2>
          <p className="mt-3 text-[16px] leading-relaxed text-stone-600">{copy.ctaBody}</p>
          <a
            href={`/${locale}#join`}
            className="mt-5 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground transition-colors duration-200 hover:bg-rust-600"
          >
            {copy.ctaButton}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
          <div className="mt-6 flex flex-wrap gap-4 text-[15px]">
            <a href={checklistPath} className="text-rust-600 underline-offset-4 hover:underline">
              {t.nav.checklist}
            </a>
            <a href={`/${locale}/guides`} className="text-rust-600 underline-offset-4 hover:underline">
              {t.nav.guides}
            </a>
            {LANDING_PAGES.filter((p) => p.id !== page.id).map((p) => (
              <a
                key={p.id}
                href={`/${locale}/${p.path[locale]}`}
                className="text-rust-600 underline-offset-4 hover:underline"
              >
                {p.content[locale].eyebrow}
              </a>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default LandingContentPage;
